import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { GuideStep } from './types';
import styles from './SetupGuide.module.css';

interface Props {
  step: GuideStep;
  done: boolean;
  /** Resolved CTA href (internal path or external URL), or null when not resolvable. */
  ctaHref: string | null;
}

/**
 * One guide step: icon, title, body, optional bullets, and an optional CTA. Completion
 * (the "mark as done" checkbox / auto "Done" pill) lives once in the section header, so it
 * sits in a consistent place rather than at the foot of each card.
 */
export function GuideStepCard({ step, done, ctaHref }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cta = step.cta;
  const showCta = cta && ctaHref;

  function handleCta() {
    if (!cta || !ctaHref) return;
    if (cta.kind === 'internal') navigate(ctaHref);
  }

  return (
    <div className={`${styles.step} ${done ? styles.stepDone : ''}`}>
      <div className={styles.stepIcon}>
        <i className={`fa-solid ${step.icon}`} aria-hidden="true" />
      </div>
      <div className={styles.stepBody}>
        <h4 className={styles.stepTitle}>{t(step.titleKey)}</h4>
        <p className={styles.stepText}>{t(step.bodyKey)}</p>

        {step.bulletKeys && step.bulletKeys.length > 0 && (
          <ul className={styles.bullets}>
            {step.bulletKeys.map((k) => (
              <li key={k}>{t(k)}</li>
            ))}
          </ul>
        )}

        {showCta && (
          <div className={styles.stepActions}>
            {cta!.kind === 'internal' ? (
              <button type="button" className={styles.cta} onClick={handleCta}>
                {t(cta!.labelKey)}
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </button>
            ) : (
              <a className={styles.cta} href={ctaHref!} target="_blank" rel="noopener noreferrer">
                {t(cta!.labelKey)}
                <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
